<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { InputGroup, Link, Typography } from '@apparatix/library/core';
import DetailEditorBase from './DetailEditorBase.vue';
import ExternalPagePopup from '@apparatix/library/ExternalPagePopup.vue';
import { useStringFunctions } from '@apparatix/composables';
import { OpportunityType } from '@apparatix/types/Leads.ts';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';

type Props = {
  audienceSelectionUrl: string;
  geoSelectionUrl: string;
  audienceSelectionValue: string[];
  geoSelectionValue: string;
  noHeadingLabels?: boolean;
  type: OpportunityType;
  isConvertingLead: boolean;
  canChangeOwner?: boolean;
}

const { isEmptyString, removeHtml } = useStringFunctions();
const props = defineProps<Props>();
const store = useOpportunityStore();

const showGeographicSelection = ref(false);
const showAudienceSelection = ref(false);

const audienceSelectionValue = ref(props.audienceSelectionValue);
const geoSelectionValue = ref(props.geoSelectionValue);

const leftColLabel = computed(() => {
  if (props.noHeadingLabels) return undefined;
  return 'Inventory Area';
});

const rightColLabel = computed(() => {
  if (store.canViewAudience[props.type]) {
    return 'Demographics';
  }
  return '';
});

const geoSelectionLabel = computed(() => {
  if (isEmptyString(geoSelectionValue.value, true)) return ['', ''];
  const [areaType, markets] = geoSelectionValue.value.split(':');

  return [`${areaType}:`, markets];
});

const audienceSelectionLabel = computed(() => audienceSelectionValue.value.join(', '));

// This is a necessary check, when clearing all audiences, array[0] = '' is returned
const audienceIsEmpty = computed(() => {
  if (audienceSelectionValue.value.length === 0) return true;

  return audienceSelectionValue.value[0] === '';
});

const handleAudienceClick = () => {
  showAudienceSelection.value = !showAudienceSelection.value;
};
const handleGeoClick = () => {
  showGeographicSelection.value = !showGeographicSelection.value;
};

const onPostMessage = (e: MessageEvent<{ id: string, value: string }>) => {
  const { data } = e;
  if (data?.id === 'update-campaign-areas') {
    geoSelectionValue.value = removeHtml(data?.value);
  }
  if (data?.id === 'update-audience-selection') {
    audienceSelectionValue.value = data.value?.split(',');
  }
};

onMounted(() => {
  window.addEventListener('message', onPostMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', onPostMessage);
});

</script>
<template>
  <DetailEditorBase :left-col-header="leftColLabel" :right-col-header="rightColLabel">
    <template #left-col>
      <InputGroup>
        <Typography variant="bold-label">
          DMAs
        </Typography>
        <ExternalPagePopup :visible="showGeographicSelection" :url="geoSelectionUrl" @update:visible="visible => showGeographicSelection = visible" />
        <Link v-show="Boolean(geoSelectionValue)" variant="form-label" @click="handleGeoClick">
          {{ geoSelectionLabel[0] || '' }} {{ geoSelectionLabel[1] || '' }}
        </Link>
        <Typography
          v-show="!Boolean(geoSelectionValue)"
          muted
          variant="form-label"
          @click="handleGeoClick"
        >
          Empty
        </Typography>
      </InputGroup>
    </template>
    <template v-if="store.canViewAudience[props.type]" #right-col>
      <InputGroup>
        <Typography variant="bold-label">
          Audiences
        </Typography>
        <ExternalPagePopup :visible="showAudienceSelection" :url="audienceSelectionUrl" @update:visible="visible => showAudienceSelection = visible" />
        <Link v-show="!Boolean(audienceIsEmpty)" variant="form-label" @click="handleAudienceClick">
          {{ audienceSelectionLabel }}
        </Link>
        <Typography
          v-show="Boolean(audienceIsEmpty)"
          muted
          variant="form-label"
          @click="handleAudienceClick"
        >
          Empty
        </Typography>
      </InputGroup>
    </template>
  </DetailEditorBase>
</template>
